/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 * 
 ********************************************************************!*/

import { ActionContext, ActionTree } from "vuex";
import { IBotState } from "./state";
import { IRootState } from "@/store/modules/root/state";
import { IMutations } from "./mutations";
import { collection, doc, getDoc, getDocs, getFirestore, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { getDatabase, get, child, ref, onValue } from "firebase/database";
import { IDialog, IFlow } from "@chatcaptain/types/dialog";
import { BotStore } from "@/store/constants";
import { IEntity, IIntent, IModel, IVersion } from "@chatcaptain/types/chatbot";

export enum Actions {
  RESET = "RESET",
  LOAD_BOT = "LOAD_BOT",
  LOAD_FLOWS = "LOAD_FLOWS",
  LOAD_FAQS = "LOAD_FAQS",
  LOAD_LANGUAGES = "LOAD_LANGUAGES",
  LOAD_ENTITIES = "LOAD_ENTITIES",
  LOAD_MODELS = "LOAD_MODELS",
  LOAD_VERSIONS = "LOAD_VERSIONS",
  LOAD_LIVE_VERSION = "LOAD_LIVE_VERSION",
  LOAD_LAST_VERSION = "LOAD_LAST_VERSION",
}

export type AugmentedActionContext = {
  commit<K extends keyof IMutations>(
    key: K,
    payload: Parameters<IMutations[K]>[1]
  ): ReturnType<IMutations[K]>;
} & Omit<ActionContext<IBotState, IRootState>, "commit">;

export interface IActions {
  [Actions.RESET]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_BOT]({ commit }: AugmentedActionContext, id: string): void;
  [Actions.LOAD_FLOWS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_FAQS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_LANGUAGES]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_ENTITIES]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_MODELS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_VERSIONS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_LIVE_VERSION]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_LAST_VERSION]({ commit }: AugmentedActionContext): void;
}

export const actions: ActionTree<IBotState, IRootState> & IActions = {
  [Actions.RESET]({ commit }) {
    commit(BotStore.Mutations.SET_FLOWS, []);
    commit(BotStore.Mutations.SET_FAQS, []);
  },
  [Actions.LOAD_BOT]({ commit }, id: string) {
    getDoc(doc(getFirestore(), "dialogs/" + id)).then(bot => {
      const data = bot.data() as IDialog;
      commit(BotStore.Mutations.SET_ID, id);
      commit(BotStore.Mutations.SET_NAME, data.name);
    });
  },
  [Actions.LOAD_FLOWS]({ commit }) {
    getDocs(collection(getFirestore(), "dialogs/" + this.getters[BotStore.Getters.GET_ID] + "/flows")).then(docs => {
      const flows: IFlow[] = [];
      if(docs) docs.forEach(doc => {
        const data = doc.data() as IFlow;
        flows.push({
          id: doc.id,
          ...data
        });
      });
      commit(BotStore.Mutations.SET_FLOWS, flows);
    });
  },
  [Actions.LOAD_FAQS]({ commit }) {
    getDocs(query(collection(getFirestore(), "dialogs/" + this.getters[BotStore.Getters.GET_ID] + "/intents"), where("faq", "==", true), orderBy("name", "asc"))).then(docs => {
      const faqs: IIntent[] = [];
      if(docs) docs.forEach(doc => {
        const data = doc.data() as IIntent;
        faqs.push({
          id: doc.id,
          ...data
        });
      });
      commit(BotStore.Mutations.SET_FAQS, faqs);
    });
  },
  [Actions.LOAD_LANGUAGES]({ commit }) {
    getDoc(doc(getFirestore(), "dialogs/" + this.getters[BotStore.Getters.GET_ID] + "/settings/languages")).then(doc => {
      const data = doc.data();
      if(data?.default) commit(BotStore.Mutations.SET_DEFAULT_LANGUAGE, data.default as string);
      commit(BotStore.Mutations.SET_LANGUAGES, data?.active as string[] || []);
    });
  },
  [Actions.LOAD_ENTITIES]({ commit }) {
    getDocs(collection(getFirestore(), "dialogs/" + this.getters[BotStore.Getters.GET_ID] + "/entities")).then(docs => {
      const entities: IEntity[] = [];
      if(docs) docs.forEach(doc => {
        const data = doc.data() as IEntity;
        entities.push({
          id: doc.id,
          ...data
        });
      });
      commit(BotStore.Mutations.SET_ENTITIES, entities);
    });
  },
  [Actions.LOAD_MODELS]({ commit }) {
    onSnapshot(collection(getFirestore(), "dialogs/" + this.getters[BotStore.Getters.GET_ID] + "/models"), snapshot => {
      const models: IModel[] = [];
      snapshot.docs.forEach(doc => {
        models.push({
          ...doc.data(),
          id: doc.id
        } as IModel);
      });
      commit(BotStore.Mutations.SET_MODELS, models);
    });
  },
  [Actions.LOAD_VERSIONS]({ commit }) {
    getDocs(query(collection(getFirestore(), "dialogs/" + this.getters[BotStore.Getters.GET_ID] + "/versions"), orderBy("created", "desc"))).then(docs => {
      const versions: IVersion[] = [];
      if(docs) docs.forEach(doc => {
        versions.push(doc.data() as IVersion);
      });
      commit(BotStore.Mutations.SET_VERSIONS, versions);
    });
  },
  [Actions.LOAD_LIVE_VERSION]({ commit }) {
    onValue(ref(getDatabase(), "dialogs/" + this.getters[BotStore.Getters.GET_ID] + "/version"), snapshot => {
      const versionId: number = snapshot.val() as number;
      if(versionId) commit(BotStore.Mutations.SET_LIVE_VERSION, versionId);
    }, err => {
      console.error("error: ", err);
    });
  },
  [Actions.LOAD_LAST_VERSION]({ commit }) {
    onSnapshot(query(collection(getFirestore(), "dialogs/" + this.getters[BotStore.Getters.GET_ID] + "/versions"), orderBy("created", "desc"), limit(1)), snapshot => {
      if(snapshot.docs.length > 0) commit(BotStore.Mutations.SET_LAST_VERSION, snapshot.docs[0].data() as IVersion);
    });
  }
}